import React, { useState, useContext, useEffect } from 'react';
import { useParams } from "react-router-dom";
import {localizeKey, localizeArt} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import Collapsible from './Collapsible';
import { Link } from 'react-router-dom';
import ShareButton from './ShareButton';
import LinkInput from './LinkInput';
import ExpandableImage from './ExpandableImage';
import { Badge, Button, Tooltip } from '@mantine/core';
import { IconBrandTelegram } from '@tabler/icons-react';

const formatLastSeen = (isoString, language) => {
  if (!isoString) return null;
  const diffMs = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const ru = language === "ru";
  if (mins < 2) return ru ? "Был(а) только что" : "Last seen just now";
  if (mins < 60) return ru ? `Был(а) ${mins} мин. назад` : `Last seen ${mins}m ago`;
  if (hours < 24) return ru ? `Был(а) ${hours} ч. назад` : `Last seen ${hours}h ago`;
  if (days < 7) return ru ? `Был(а) ${days} дн. назад` : `Last seen ${days}d ago`;
  const date = new Date(isoString).toLocaleDateString(ru ? "ru-RU" : "en-US", { day: "2-digit", month: "short", year: "numeric" });
  return ru ? `Был(а) ${date}` : `Last seen ${date}`;
};

const TextUserPair = ({user, userId, hideRecomInput, hideTgBadge, hideUserName}) => {
  const myUser = useContext(MyUserContext);

  const [curUser, setCurUser] = useState(user);

  const [inputText, setInputText] = useState('');

  // Status of recent recommendation.
  const [errText, setErrText] = useState(null);

  const [successText, setSuccessText] = useState(null);
  const [successResp, setSuccessResp] = useState(null);

  const fetchCurUser = () => {
    axiosInstance
    .get(`/users/${userId}`)
    .then((response) => {
      setCurUser(response.data);
    })
    .catch((error) => {
      console.error(error);
    });
  };

  const handleRecommend = (userId, link) => {
    axiosInstance
    .post('/postRecommendation', { toUserId: userId, wikiArticleLink: link })
    .then((response) => {
      setSuccessText(localizeKey('recommendationPostSuccess', language));
      setErrText(null);
      setSuccessResp(response.data);
      setInputText("");
    })
    .catch((error) => {
      setErrText(error.response.data);
      setSuccessText(null);
      setSuccessResp(null);
    });
  }

  const handleFollow = () => {
    let verb = "follow";
    if (curUser.followedByMeState === "approved" || curUser.followedByMeState === "requested") {
      verb = "unfollow";
    }
    axiosInstance
    .post(`users/${curUser.id}/${verb}`, {})
    .then((response) => {
      setCurUser(response.data);
    })
    .catch((error) => {
      console.error(error);
    })
  }
  const renderButtonTextLocalizeKey = (followedByMeState) => {
    switch(followedByMeState) {
      case 'none':
        return 'follow';
      case 'requested':
        return 'requested';
      case 'approved':
        return 'approved';
      default:
        console.log.error("undefined followedByMeState");
        return "undefined";
    }
  }

  const handleApproveFollow = () => {
    axiosInstance
    .post(`users/${curUser.id}/respondToFollowRequest`, { decision : "approve" })
    .then((response) => {
      setCurUser(response.data);
    })
    .catch((error) => {
      console.error(error);
    })
  }
  const handleRejectFollow = () => {
    axiosInstance
    .post(`users/${curUser.id}/respondToFollowRequest`, { decision : "reject" })
    .then((response) => {
      setCurUser(response.data);
    })
    .catch((error) => {
      console.error(error);
    })
  }

  if (!curUser) {
    fetchCurUser();
    return <div></div>;
  }

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  return (
    <div>
      <div className="flex items-center">
        <Link to={`/users/${curUser.id}/likes`}>
          <ExpandableImage src={curUser.avatarUrl} />
        </Link>
        &nbsp;&nbsp;
        <div className="flex flex-col items-start gap-1">
          {!hideUserName && (
            <Link className="flex items-center text-center" to={`/users/${curUser.id}/likes`}>
              <p className="text-base font-semibold leading-6 hover:underline min-w-min">
                {curUser.userName + (myUser && myUser.myUserInfo.myUserId === curUser.id ? " (me)" : "")}
              </p>
              {
              (myUser && curUser.followMeState === "approved") &&
                <>&nbsp;({ language==="ru" ? "подписан(а) на вас" : "follows you"})</>
              }
              {/* The inline '71%' here duplicated the big '71% совпадение
                  вкусов' row rendered below the user card — removed. */}
            </Link>
          )}
          {curUser.lastSeenTime && (
            <div className="text-xs" style={{ color: 'var(--mantine-color-dimmed)' }}>
              {formatLastSeen(curUser.lastSeenTime, language)}
            </div>
          )}
          {curUser.hasTelegramNotifications && !hideTgBadge && (
            <Tooltip label={localizeKey('telegramNotificationsTooltip', language)} withArrow>
              <Badge
                className="mt-2"
                size="xs"
                color="blue"
                variant="light"
                leftSection={<IconBrandTelegram size={10} />}
              >
                {localizeKey('telegramNotificationsBadge', language)}
              </Badge>
            </Tooltip>
          )}
          {
          (myUser && myUser.myUserInfo.myUserId !== curUser.id) && 
          <Button
            onClick={handleFollow}
            variant={(curUser.followedByMeState === "approved" || curUser.followedByMeState === "requested") ? "outline" : "filled"}
            color="indigo"
            size="compact-sm"
          >
            {localizeKey(renderButtonTextLocalizeKey(curUser.followedByMeState), language)}
          </Button>
          }
        </div>
        {
        myUser &&
        <div className="-mt-1.5">
          {
          curUser.followMeState === "requested" &&
          <>
            <div>{language === "ru" ? "Хочет подписаться" : "Wants to follow"}</div>
            <div className="flex gap-2">
              <Button onClick={handleApproveFollow} variant="filled" color="indigo" size="compact-sm">
                Approve
              </Button>
              <Button onClick={handleRejectFollow} variant="outline" color="indigo" size="compact-sm">
                Reject
              </Button>
            </div>
          </>
          }
        </div>
        }
      </div>      
    </div>
  )
};

export default TextUserPair;